import ScatterPlot, {DataPoint} from "../Charts/ScatterPlot";
import Barchart, {BarData} from "../Charts/Barchart";
import React, {useContext} from "react";
import TabTitle from "../tabTitle/TabTitle";
import './BranchAnalysisStyles.css'
import styled from "styled-components";
import {GlobalDataContext} from "../../contexts/GlobalDataContext";
import EuropeRecord from "../../models/EuropeRecord";
import Branch from "../../interfaces/Branch";
import {useTranslation} from "react-i18next";
import {RegionSelectedContext} from "../../contexts/RegionSelectedContext";
import TabHeader from "../tabHeader/TabHeader";
import * as d3 from 'd3';
import HeaderContainer from "../Commons/HeaderContainer";
import BodyContainer from "../Commons/BodyContainer";
import BranchChartWithTitle from "../Commons/BranchChartWithTitle";
import BranchChartRow from "../Commons/BranchChartRow";
import LegendComponent from "../LegendsGraphics/LegendGraphic";
import Images from "../../assets/images/Images";
// @ts-ignore
import {animateScroll} from "react-scroll";

const BranchAnalysisContent = styled.div`
  height: 100%;

  @media (min-width: 481px) {
    display: flex;
    flex-direction: column;
    flex: 1;
    margin-top: 50px;
  }

  @media (max-width: 480px) {
    display: flex;
    flex-direction: column;
    flex: 1;
    margin-top: 20px;
  }
`;

const recordToDataPoint = (record: EuropeRecord) => {
  return {
    x: record.premiums_last_10_years,
    y: record.pct_growth_last_10_years,
    trend: record.trend,
    label: record.company_group
  };
};

const parseEuropeRecords = (records: EuropeRecord[]): [BarData[], DataPoint[]] => {
  const barChartRecords: BarData[] = records.map((record: EuropeRecord) => {
    return {
      group: record.company_group,
      bars: [record.premiums_last_10_years,
             record.increment_last_9_years,
             record.increment_last_year],
      point: record.premiums_current_year
    };
  });

  const scatterPlotRecords: DataPoint[] = records.map(recordToDataPoint);

  return [barChartRecords, scatterPlotRecords];
};

const LifeRanking = () => {
    const { t } = useTranslation();

    // @ts-ignore
    const { europeRecords } = useContext(GlobalDataContext);
    // @ts-ignore
    const { yearSelected } = useContext(RegionSelectedContext);

    if (Object.keys(europeRecords).length === 0) {
      return (<div/>);
    }

    const totalRecords: EuropeRecord[] =
      europeRecords.filter((record: EuropeRecord) => record.branch === Branch.LIFE);

    const totalDataPoints: DataPoint[] = totalRecords.map(recordToDataPoint);

    // @ts-ignore
    const xExtent: [number, number] = d3.extent(totalDataPoints.map(point => point.x));
    // @ts-ignore
    const yExtent: [number, number] = d3.extent(totalDataPoints.map(point => point.y));

    const records: EuropeRecord[] = totalRecords
      .filter((record: EuropeRecord) => record.year === yearSelected);

    const [barChartRecords, scatterPlotRecords] = parseEuropeRecords(records);

    const lifeCharts = [
      <BranchChartWithTitle
        key={ 1 }
        title={ t('branchAnalysis.lifeSection.leftChartTitle', { year: yearSelected }) }
        chartLegend={ <LegendComponent isLight={ true } /> }
        chart={
          <Barchart
            bars={ barChartRecords }
            isDark={ true }
            barColors={['#8B8B8B', '#434343', '#FAA3A3']}
          />
        }
      />,
      <BranchChartWithTitle
        key={ 2 }
        title={ t('branchAnalysis.lifeSection.rightChartTitle', { yearStart: yearSelected - 10, yearEnd: yearSelected }) }
        chartLegend={ <div style={{ height: 60 }} /> }
        chart={
          <ScatterPlot
            points={ scatterPlotRecords }
            isDark={ true }
            formatVerticalTicks={ (n) => `${Math.round(n * 100)}%` }
            xExtent={ xExtent }
            yExtent={ yExtent }
          />
        }
      />
    ];

    return (
        <BranchChartRow
          rowTitle={ t('branchAnalysis.lifeSection.title') }
          charts={ lifeCharts }
        />
    )
}

const StyledLifeRanking = styled(LifeRanking)`
  flex: 1 0 auto;
  height: 80%;
`;

const NoLifeRanking = () => {
    const { t } = useTranslation();

    // @ts-ignore
    const { europeRecords } = useContext(GlobalDataContext);
    // @ts-ignore
    const { yearSelected } = useContext(RegionSelectedContext);

    if (Object.keys(europeRecords).length === 0) {
      return (<div/>);
    }

    const totalRecords: EuropeRecord[] =
      europeRecords.filter((record: EuropeRecord) => record.branch === Branch.NO_LIFE);

    const totalDataPoints: DataPoint[] = totalRecords.map(recordToDataPoint);

    // @ts-ignore
    const xExtent: [number, number] = d3.extent(totalDataPoints.map(point => point.x));
    // @ts-ignore
    const yExtent: [number, number] = d3.extent(totalDataPoints.map(point => point.y));

    const records: EuropeRecord[] = totalRecords
      .filter((record: EuropeRecord) => record.year === yearSelected);

    const [barChartRecords, scatterPlotRecords] = parseEuropeRecords(records);

    const noLifeCharts = [
      <BranchChartWithTitle
        key={ 1 }
        title={ t('branchAnalysis.noLifeSection.leftChartTitle', { year: yearSelected }) }
        chartLegend={ <LegendComponent isLight={ true } /> }
        chart={
          <Barchart
            bars={ barChartRecords }
            isDark={ true }
            barColors={ ['#8B8B8B', '#434343'] }
          />
        }
      />,
      <BranchChartWithTitle
        key={ 2 }
        title={ t('branchAnalysis.noLifeSection.rightChartTitle', { yearStart: yearSelected - 10, yearEnd: yearSelected }) }
        chartLegend={ <div style={{ height: 60 }} /> }
        chart={
          <ScatterPlot
            points={ scatterPlotRecords }
            isDark={ true }
            formatVerticalTicks={ (n) => `${Math.round(n * 100)}%` }
            xExtent={ xExtent }
            yExtent={ yExtent }
          />
        }
      />
    ];


    return (
        <BranchChartRow
          rowTitle={ t('branchAnalysis.noLifeSection.title') }
          charts={ noLifeCharts }
        />
    );
};

const StyledNoLifeRanking = styled(NoLifeRanking)`
  flex: 1 0 auto;
  height: 80%;
`;

const StyledBodyContainer = styled(BodyContainer)`
  height: 90%;
`;

const BranchAnalysisComponent: React.FC<{ isTabSelected: boolean }> = ({isTabSelected}) => {
    const { t } = useTranslation();

    return (
        <div id={'BranchAnalysisContainer'} className={(isTabSelected ? 'openContainerBA' : 'closeContainerBA')}>
            {
                isTabSelected &&
                <HeaderContainer>
                    <TabHeader darkMode={true}/>
                </HeaderContainer>
            }
            <TabTitle
                title={ t('branchAnalysis.title') }
                isTabSelected={isTabSelected}
                darkMode={true}
            />
            <StyledBodyContainer id='scrollContainer' className='scrollContainer'>
                {
                    isTabSelected &&
                    <BranchAnalysisContent id={'CUSTOMID'}>
                        <StyledLifeRanking />
                        <StyledNoLifeRanking />
                        <div className={'scollContainer'}>
                            <span
                              className={'homeScrollUpLogo'}
                              onClick={() => {animateScroll.scrollToTop({containerId: 'scrollContainer'})}}
                            >
                              <span className='arrow' />
                            </span>
                        </div>
                    </BranchAnalysisContent>
                }
            </StyledBodyContainer>
        </div>
    )
};

export default BranchAnalysisComponent;
